import { useState, useEffect } from "react";
import { Sparkles, Send, Trash2, Copy } from "lucide-react";
import { api, fmt, mediaUrl } from "../../lib/api";
import { toast } from "sonner";

/* ----------------- DAILY HEAT — scheduled email broadcasts ----------------- */
export function DailyHeatTab() {
  const [rows, setRows] = useState([]);
  const [sizes, setSizes] = useState({ leads: 0, members: 0, both: 0 });
  const [f, setF] = useState({ subject: "", body_md: "", audience: "leads", send_at: "" });
  const load = () => api.get("/admin/broadcasts").then((r) => { setRows(r.data.broadcasts); setSizes(r.data.audience_sizes); });
  useEffect(() => { load(); }, []);
  const create = async (sendNow) => {
    if (!f.subject.trim() || !f.body_md.trim()) { toast.error("Subject and body required"); return; }
    const payload = { subject: f.subject, body_md: f.body_md, audience: f.audience, send_at: sendNow ? null : (f.send_at ? new Date(f.send_at).toISOString() : null) };
    try {
      const { data } = await api.post("/admin/broadcasts", payload);
      if (sendNow) { await api.post(`/admin/broadcasts/${data.broadcast.id}/send`); toast.success("Sent via Resend"); }
      else toast.success(payload.send_at ? "Scheduled" : "Saved as draft");
      setF({ subject: "", body_md: "", audience: "leads", send_at: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const sendNow = async (id) => { try { await api.post(`/admin/broadcasts/${id}/send`); toast.success("Sent"); load(); } catch { toast.error("Send failed"); } };
  const del = async (id) => { await api.delete(`/admin/broadcasts/${id}`); load(); };
  const suggest = async () => {
    try {
      const { data } = await api.get("/admin/broadcasts/suggestion");
      setF({ ...f, subject: data.subject, body_md: data.body_md });
      toast.success(data.source === "win" ? "Drafted from a recent member win" : data.source === "article" ? "Drafted from your latest blog post" : "Starter prompt loaded");
    } catch { toast.error("Could not generate draft"); }
  };
  return (
    <div className="space-y-6" data-testid="daily-heat-tab">
      <div className="panel p-6">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="overline">// WRITE TODAY'S DAILY HEAT</div>
          <button data-testid="heat-suggest" onClick={suggest} className="btn-ghost text-xs"><Sparkles className="w-3 h-3"/>Generate today's draft</button>
        </div>
        <p className="text-textMuted text-sm mb-4">One short, sharp transmission keeps your list warm and converting. Tap <em>Generate today's draft</em> to auto-pull a recent member win or your latest blog post into a ready-to-edit draft — no blank page. Send now, or schedule it. We email you a reminder each day if nothing's queued. Delivered via Resend.</p>
        <input data-testid="heat-subject" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Subject line — make them open it" />
        <textarea data-testid="heat-body" rows={6} className="mt-3" value={f.body_md} onChange={(e) => setF({ ...f, body_md: e.target.value })} placeholder={"Write the heat. Separate paragraphs with a blank line.\n\nOne idea. One CTA. Send."} />
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <div>
            <label className="overline">// AUDIENCE</label>
            <select data-testid="heat-audience" value={f.audience} onChange={(e) => setF({ ...f, audience: e.target.value })}>
              <option value="leads">Email leads ({sizes.leads})</option>
              <option value="members">Members ({sizes.members})</option>
              <option value="both">Everyone ({sizes.both})</option>
            </select>
          </div>
          <div><label className="overline">// SCHEDULE FOR (optional)</label><input type="datetime-local" value={f.send_at} onChange={(e) => setF({ ...f, send_at: e.target.value })} /></div>
        </div>
        <div className="flex flex-wrap gap-3 mt-4">
          <button data-testid="heat-send-now" onClick={() => create(true)} className="btn-gold"><Send className="w-4 h-4"/>Send now</button>
          <button data-testid="heat-schedule" onClick={() => create(false)} className="btn-ghost">{f.send_at ? "Schedule" : "Save draft"}</button>
        </div>
      </div>

      <div className="space-y-2">
        {rows.map((b) => (
          <div key={b.id} className="panel p-4 flex items-start justify-between gap-4" data-testid={`broadcast-${b.id}`}>
            <div>
              <div className="flex items-center gap-2"><span className="overline">{b.status}</span><span className="text-textDim text-xs font-mono">{b.audience}{b.sent_count ? ` · ${b.sent_count} sent` : ""}</span></div>
              <div className="font-display text-xl text-cream mt-1">{b.subject}</div>
              <div className="text-textMuted text-sm mt-1 line-clamp-2">{b.body_md}</div>
              {b.send_at && b.status === "scheduled" && <div className="text-xs font-mono text-gold mt-1">⏱ {fmt.datetime(b.send_at)}</div>}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {b.status !== "sent" && <button onClick={() => sendNow(b.id)} className="btn-ghost text-xs">Send now</button>}
              <button onClick={() => del(b.id)} className="text-ruby hover:text-red-400"><Trash2 className="w-4 h-4"/></button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-textMuted">No broadcasts yet. Write your first one above.</p>}
      </div>
    </div>
  );
}

/* ----------------- FREE FACEBOOK GROUP MARKETING ----------------- */
export function FreeGroupTab() {
  const [d, setD] = useState(null);
  const [clicks, setClicks] = useState(0);
  const [leads, setLeads] = useState(0);
  const [bens, setBens] = useState("");
  const load = () => api.get("/admin/fb-group").then((r) => {
    setD(r.data.fb_group); setClicks(r.data.clicks); setLeads(r.data.leads);
    setBens((r.data.fb_group.benefits || []).join("\n"));
  });
  useEffect(() => { load(); }, []);
  if (!d) return null;
  const save = async () => {
    const payload = { ...d, member_count: parseInt(d.member_count) || 0, benefits: bens.split("\n").map((s) => s.trim()).filter(Boolean) };
    await api.post("/admin/fb-group", payload);
    toast.success("Free group marketing updated");
    load();
  };
  const set = (k, v) => setD({ ...d, [k]: v });
  return (
    <div className="space-y-6" data-testid="free-group-tab">
      <div className="grid md:grid-cols-3 gap-px bg-borderGold">
        <div className="bg-void p-6"><div className="overline mb-2">// GROUP CLICKS</div><div className="font-display text-5xl text-cream">{clicks}</div><div className="text-xs font-mono text-textDim mt-1">CTA clicks across the site</div></div>
        <div className="bg-void p-6"><div className="overline mb-2">// EMAIL LEADS</div><div className="font-display text-5xl text-cream">{leads}</div><div className="text-xs font-mono text-textDim mt-1">Opt-ins captured</div></div>
        <div className="bg-void p-6"><div className="overline mb-2">// STATUS</div><label className="flex items-center gap-2 mt-3 text-sm text-cream"><input data-testid="fb-enabled" type="checkbox" checked={!!d.enabled} onChange={(e) => set("enabled", e.target.checked)} style={{width:16,height:16}}/> Show across the site</label></div>
      </div>

      <div className="panel p-6 space-y-4">
        <div className="overline">// HOW THE FREE GROUP IS MARKETED</div>
        <p className="text-textMuted text-sm">This drives your highest-leverage top-of-funnel: a free yes is far easier than a paid one. Set a real member count for social proof, tune the copy, and the CTA appears on your Landing, Blog, and Pricing pages. Every click is tracked above.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <div><label className="overline">// GROUP URL</label><input data-testid="fb-url" value={d.url || ""} onChange={(e) => set("url", e.target.value)} placeholder="https://www.facebook.com/groups/..."/></div>
          <div><label className="overline">// GROUP NAME</label><input value={d.name || ""} onChange={(e) => set("name", e.target.value)}/></div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div><label className="overline">// EYEBROW LABEL</label><input value={d.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)}/></div>
          <div><label className="overline">// MEMBER COUNT (social proof)</label><input data-testid="fb-count" type="number" value={d.member_count ?? 0} onChange={(e) => set("member_count", e.target.value)}/></div>
        </div>
        <div><label className="overline">// HEADLINE</label><input value={d.headline || ""} onChange={(e) => set("headline", e.target.value)}/></div>
        <div><label className="overline">// SUBHEAD</label><textarea rows={2} value={d.subhead || ""} onChange={(e) => set("subhead", e.target.value)}/></div>
        <div><label className="overline">// BENEFITS (one per line)</label><textarea rows={4} value={bens} onChange={(e) => setBens(e.target.value)}/></div>
        <div><label className="overline">// CTA BUTTON LABEL</label><input value={d.cta_label || ""} onChange={(e) => set("cta_label", e.target.value)}/></div>
        <div className="flex flex-wrap gap-4 pt-2">
          <label className="flex items-center gap-2 text-sm text-textMuted"><input type="checkbox" checked={!!d.show_on_landing} onChange={(e) => set("show_on_landing", e.target.checked)} style={{width:16,height:16}}/> Landing</label>
          <label className="flex items-center gap-2 text-sm text-textMuted"><input type="checkbox" checked={!!d.show_on_blog} onChange={(e) => set("show_on_blog", e.target.checked)} style={{width:16,height:16}}/> Blog</label>
          <label className="flex items-center gap-2 text-sm text-textMuted"><input type="checkbox" checked={!!d.show_on_pricing} onChange={(e) => set("show_on_pricing", e.target.checked)} style={{width:16,height:16}}/> Pricing</label>
        </div>
        <button data-testid="fb-save" onClick={save} className="btn-gold">Save free-group marketing</button>
      </div>
    </div>
  );
}

/* ----------------- AD COPY GENERATOR ----------------- */
export function AdCopyTab() {
  const [type, setType] = useState("drop");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [variants, setVariants] = useState([]);
  const [publicFlag, setPublicFlag] = useState(true);
  useEffect(() => {
    const ep = type === "drop" ? "/admin/drops" : "/admin/articles";
    api.get(ep).then((r) => setItems(type === "drop" ? r.data.drops : r.data.articles));
    setSelected(null); setVariants([]);
  }, [type]);
  const generate = async () => {
    if (!selected) return;
    const ep = type === "drop" ? `/admin/ad-copy/drop/${selected.id}` : `/admin/ad-copy/article/${selected.id}`;
    const { data } = await api.post(ep, { public: publicFlag });
    setVariants(data.variants);
  };
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copied"); };
  return (
    <div>
      <div className="overline mb-3">// AD COPY GENERATOR · FB / IG / X / EMAIL READY</div>
      <p className="text-textMuted text-sm mb-6">Pick any drop or article. We'll surface 5 high-converting copy variants you can paste straight into your group, socials, or DMs.</p>
      <div className="flex gap-3 mb-4">
        <button onClick={() => setType("drop")} className={type === "drop" ? "btn-gold" : "btn-ghost"}>Drops</button>
        <button onClick={() => setType("article")} className={type === "article" ? "btn-gold" : "btn-ghost"}>Articles</button>
        <label className="flex items-center gap-2 text-sm text-textMuted ml-auto"><input type="checkbox" checked={publicFlag} onChange={(e) => setPublicFlag(e.target.checked)} style={{width:16,height:16}}/>Public funnel angle</label>
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div className="space-y-2 md:col-span-1">
          {items.map((d) => (
            <button key={d.id} data-testid={`adcopy-pick-${d.id}`} onClick={() => setSelected(d)} className={`block w-full text-left panel p-3 ${selected?.id === d.id ? "border-gold" : ""}`}>
              <div className="text-cream">{d.title}</div>
              <div className="overline mt-1">{d.published ? "PUBLISHED" : "DRAFT"}</div>
            </button>
          ))}
        </div>
        <div className="md:col-span-2 space-y-3">
          {selected ? (
            <>
              <button onClick={generate} className="btn-gold">Generate 5 variants</button>
              {variants.map((v, i) => (
                <div key={`${v.platform}-${i}`} className="panel p-4">
                  <div className="overline mb-2">{v.platform}</div>
                  <pre className="whitespace-pre-wrap text-cream/95 text-sm font-body">{v.copy}</pre>
                  <button onClick={() => copy(v.copy)} className="btn-ghost text-xs mt-3"><Copy className="w-3 h-3"/>Copy</button>
                </div>
              ))}
            </>
          ) : <p className="text-textMuted text-sm">Pick a {type} on the left.</p>}
        </div>
      </div>
    </div>
  );
}

/* ----------------- TESTIMONIALS ADMIN ----------------- */
export function TestimonialsAdmin() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data.testimonials));
  useEffect(() => { load(); }, []);
  const moderate = async (id, status) => { await api.patch(`/admin/testimonials/${id}`, { status }); toast.success(status); load(); };
  return (
    <div>
      <div className="overline mb-3">// {items.length} testimonials · {items.filter(t => t.status === "pending").length} pending</div>
      <div className="space-y-3">
        {items.map((t) => (
          <div key={t.id} data-testid={`adm-test-${t.id}`} className="panel p-5">
            <div className="overline mb-2">{t.status?.toUpperCase()} · {fmt.datetime(t.created_at)}</div>
            {t.headline && <div className="font-display text-xl text-gold">"{t.headline}"</div>}
            {t.image_url && <img src={mediaUrl(t.image_url)} alt="" className="max-h-48 object-cover border border-borderGold mt-2"/>}
            <p className="text-cream/90 whitespace-pre-wrap mt-2">{t.body}</p>
            <div className="flex gap-2 mt-4">
              <button onClick={() => moderate(t.id, "approved")} className="btn-ghost text-xs">Approve</button>
              <button onClick={() => moderate(t.id, "featured")} className="btn-gold text-xs">Feature</button>
              <button onClick={() => moderate(t.id, "rejected")} className="btn-ghost text-xs !border-ruby !text-ruby">Reject</button>
              <a href={`/u/${t.user_id}`} target="_blank" rel="noreferrer" className="overline self-center ml-2">from {t.user_name} →</a>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="text-textMuted">No testimonials yet.</p>}
      </div>
    </div>
  );
}
